/** Static copy from PCL 25-26 31-08-26.xlsm — sheets V1 / V2 / V2.1 / V3 / V4.1 */

import type {G0Page} from '@/pages/components/audit-program/g-section/constants';

export type VSectionPageCode = 'V1' | 'V2' | 'V2.1' | 'V3' | 'V4.1';

export const V_SECTION_PAGES: VSectionPageCode[] = ['V1', 'V2', 'V2.1', 'V3', 'V4.1'];

export const V_PAGE_TITLES: Record<VSectionPageCode, string> = {
    V1: 'Audit programme - General ledger',
    V2: 'Testing journal entries',
    'V2.1': 'Detailed testing of journals',
    V3: 'Review of accounting estimates for bias',
    'V4.1': 'Detailed testing of journals (alt)',
};

export type VProgrammePage = G0Page;

export const V1_PAGE: VProgrammePage = {
    title: 'AUDIT PROGRAMME - GENERAL LEDGER',
    rows: [
        {id: 'r1', kind: 'section', num: '', text: 'General', level: 0},
        {id: 'r2', kind: 'main', num: '1', text: 'Agree the opening balances to last year\'s accounts.', level: 0},
        {id: 'r3', kind: 'main', num: '2', text: 'Agree or reconcile the financial statements with the underlying accounting records.', level: 0},
        {id: 'r4', kind: 'main', num: '3', text: 'Review for items above performance materiality or that are otherwise unusual and verify.', level: 0},
        {id: 'r5', kind: 'section', num: '', text: 'Opening Balances', level: 0},
        {id: 'r6', kind: 'main', num: '4', text: 'Consider whether:', level: 0},
        {id: 'r7', kind: 'sub', num: '(a)', text: 'The comparative information agrees with the amounts and other disclosures presented in the prior period or, when appropriate, have been restated.', level: 1},
        {id: 'r8', kind: 'sub', num: '(b)', text: 'The accounting policies reflected in the comparative information are consistent with those applied in the current period', level: 1},
        {id: 'r9', kind: 'sub', num: '(c)', text: 'Changes in accounting policies have been properly accounted for and adequately presented and disclosed.', level: 1},
        {id: 'r10', kind: 'main', num: '5', text: 'Investigate any apparent misstatements in the comparative figures to determine whether there is a material impact on the current period accounting or disclosure.', level: 0},
        {id: 'r11', kind: 'section', num: '', text: 'Journals', level: 0},
        {id: 'r12', kind: 'main', num: '6', text: 'Test the appropriateness of journal entries recorded in the general ledger and other adjustments made in the preparation of the financial statements including:', level: 0},
        {id: 'r13', kind: 'sub', num: '(a)', text: 'Making inquiries of individuals involved in the financial reporting process about inappropriate or unusual activity relating to the processing of journal entries and other adjustments;', level: 1},
        {id: 'r14', kind: 'sub', num: '(b)', text: 'Selecting journal entries and other adjustments made at the end of a reporting period; and', level: 1},
        {id: 'r15', kind: 'sub', num: '(c)', text: 'Considering the need to test journal entries and other adjustments throughout the period.', level: 1},
        {id: 'r16', kind: 'sub', num: '(d)', text: 'Documenting the basis of sample selection and the details of the journals tested.', level: 1},
        {id: 'r17', kind: 'section', num: '', text: 'Accounting Estimates', level: 0},
        {id: 'r18', kind: 'main', num: '7', text: 'Review accounting estimates for bias and evaluate whether the circumstances producing the bias, if any, represent a risk of material misstatement due to fraud.', level: 0},
        {id: 'r19', kind: 'main', num: '8', text: 'As part of the above review:', level: 0},
        {id: 'r20', kind: 'sub', num: '(a)', text: 'Evaluate whether the judgments and decisions made by management in making the accounting estimates included in the financial statements, even if they are individually reasonable, indicate a possible bias on the part of the entity\'s management that may represent a risk of material misstatement due to fraud.', level: 1},
        {id: 'r21', kind: 'sub', num: '(b)', text: 'Where there are indications of bias, re-evaluate the assessment of all accounting estimates.', level: 1},
        {id: 'r22', kind: 'sub', num: '(c)', text: 'Perform a retrospective review of management judgments and assumptions related to significant accounting estimates reflected in the financial statements of the prior year.', level: 1},
        {id: 'r23', kind: 'main', num: '9', text: 'Consider whether differences between actual results and previous estimates have been quantified and that, where necessary, appropriate adjustments or disclosures made if it represents a misstatement of the previous period\'s financial statements under the applicable financial reporting framework. (See paragraph A60 of HKSA 540 (Revised).)', level: 0},
        {id: 'r24', kind: 'section', num: '', text: 'Bespoke tests', level: 0},
        {id: 'r25', kind: 'main', num: '10', text: 'Draft and perform any necessary additional tests such as those required by C8.3 and cross reference with the objectives on the summary sheet.', level: 0},
        {id: 'r26', kind: 'section', num: '', text: 'Conclusion', level: 0},
        {id: 'r27', kind: 'main', num: '11', text: 'Review the planned extent of reliance on internal controls in this area and consider whether this remains appropriate.', level: 0},
        {id: 'r28', kind: 'main', num: '12', text: 'Consider whether there are any points which need to be included in a letter of representation or letter of comment and record on A5 or A6 as appropriate.', level: 0},
        {id: 'r29', kind: 'main', num: '13', text: 'Assess whether the initial materiality and/or risk assessment should be revised in view of the audit evidence obtained. Record details of any necessary adjustments on B6 or B9. Consider the impact on the remainder of the audit work and on any work undertaken to date.', level: 0},
    ],
};

export type VQaField = {id: string; label: string};

export type V2Page = {
    title: string;
    intro: string;
    fields: VQaField[];
};

export const V2_PAGE: V2Page = {
    title: 'TESTING JOURNAL ENTRIES: WORKING PAPER',
    intro: 'HKSA 240.33(a) requires the testing of journal entries and other adjustments made in the preparation of the financial statements as part of the response to management override of controls. This includes: (i) Making inquiries of individuals involved in the financial reporting process about inappropriate or unusual activity relating to the processing of journal entries and other adjustments; (i) Testing the appropriateness of selected journal entries and other adjustments made at the end of a reporting period. (ii) Considering the need to test journal entries and other adjustments throughout the period. Proper completion of this working paper will help satisfy those requirements.',
    fields: [
        {id: 'q1', label: 'What is the objective of test?'},
        {id: 'q2', label: 'How has completeness of the population for testing been ensured?'},
        {id: 'q3', label: 'What criteria have been used for review and testing?'},
        {id: 'q4', label: 'What are the results of inquiries with individuals involved in the financial reporting process about unusual or inappropriate journals?'},
        {id: 'q5', label: 'Consider the need to test journal entries and other adjustments throughout the period and document your considerations.'},
        {id: 'q6', label: 'What review and testing procedures have been performed?'},
        {id: 'q7', label: 'Document details of the journals tested and the results on V4.1.'},
        {id: 'q8', label: 'Conclusion'},
    ],
};

export type VJournalPage = {
    title: string;
    note: string;
    blankRows: number;
};

export const V2_1_PAGE: VJournalPage = {
    title: 'DETAILED TESTING OF JOURNALS',
    note: 'This schedule should be used to record the results of detailed testing of journal entries.',
    blankRows: 10,
};

export const V4_1_PAGE: VJournalPage = {
    title: 'DETAILED TESTING OF JOURNALS',
    note: 'This schedule should be used to record the results of detailed testing of journal entries.',
    blankRows: 10,
};

export type V3Page = {
    title: string;
    blankRows: number;
    columns: Array<{id: string; label: string}>;
};

export const V3_PAGE: V3Page = {
    title: 'REVIEW OF ACCOUNTING ESTIMATES FOR BIAS',
    blankRows: 8,
    columns: [
        {id: 'estimate', label: "Accounting estimate"},
        {id: 'lastYear', label: "Last year HK$'000"},
        {id: 'actual', label: "Actual cost HK$'000"},
        {id: 'thisYear', label: "This year HK$'000"},
        {id: 'ourEstimate', label: "Our estimate HK$'000"},
        {id: 'variance', label: "Variance HK$'000"},
        {id: 'comments', label: 'Comments'},
    ],
};
